import { useRef, useCallback, MutableRefObject } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getUtmParams, getDeviceType } from './useQuizTracking';

const questionToColumn: Record<string, string> = {
  'Wat is je leeftijd? 🤔': 'Leeftijd',
  'Wat wil je het liefst verbeteren? ✨': 'Wat wil je verbeteren',
  'Hoe diep zijn je rimpels?': 'Rimpels diepte',
  'Waar wil je aan werken? ✨': 'Waar werken',
  'Hoe vaak kom je in de zon? ☀️': 'Hoevaak zon',
  'Hoeveel slaap krijg je meestal? 💤': 'Slaap',
  'Hoe vaak ervaar je stress? 😓': 'Stress',
  'Wat is je huidtint?': 'Huidtint',
  'Hoe gevoelig is je huid?': 'Gevoeligheid',
  'Welke oplossingen heb je eerder geprobeerd?': 'Eerdere oplossingen',
  'Hoeveel tijd kun je besteden aan huidverzorging? ⏱️': 'Tijd huidverzorging',
  'Heb je ooit het gevoel gehad dat je huid je ouder doet lijken dan je je van binnen voelt? 🤔': 'Gevoel ouder',
  'Hoe zou het voelen om weer een stralende, jongere versie van jezelf in de spiegel te zien?': 'Stralende versie',
  'Heb je binnenkort een belangrijke gelegenheid?': 'Gelegenheid',
};

export function useSheetSync(
  sessionIdRef: MutableRefObject<string>,
  startTimeRef: MutableRefObject<number>,
  answersRef: MutableRefObject<Record<string, { question: string; answer: string[] }>>,
  backClicksRef: MutableRefObject<number>,
  scrollDepthRef: MutableRefObject<number>,
  screenTimesRef: MutableRefObject<Record<string, number>>,
  currentScreen: number,
) {
  const syncQueueRef = useRef<Promise<void>>(Promise.resolve());
  const syncInFlightRef = useRef(false);
  const syncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSyncRef = useRef<{ isCompleted: boolean; destinationScreen: number } | null>(null);
  const sheetRowRef = useRef<number | null>(
    (() => {
      const stored = sessionStorage.getItem(`sheetRow_${sessionIdRef.current}`);
      return stored ? parseInt(stored) : null;
    })()
  );

  const executeSync = useCallback((isCompleted: boolean, destinationScreen: number) => {
    syncQueueRef.current = syncQueueRef.current.then(async () => {
      if (syncInFlightRef.current) return;
      syncInFlightRef.current = true;
      try {
        const totalDuration = Math.round((Date.now() - startTimeRef.current) / 1000);
        const utm = getUtmParams();
        const sheetData: Record<string, string> = {
          'Datum': '',
          'Voltooid': isCompleted ? 'Ja' : 'Nee',
          'Doorlooptijd': String(totalDuration),
          'Apparaat': getDeviceType(),
          'UTM Source': utm.utmSource,
          'UTM Medium': utm.utmMedium,
          'UTM Campaign': utm.utmCampaign,
          'Terug-kliks': String(backClicksRef.current),
          'Scroll diepte': `${scrollDepthRef.current}%`,
          'Schermtijden': JSON.stringify(screenTimesRef.current),
          'Laatste scherm': String(destinationScreen),
          'Afhaak scherm': isCompleted ? '' : `Scherm ${destinationScreen}`,
        };
        for (const [_, answerData] of Object.entries(answersRef.current)) {
          const colName = questionToColumn[answerData.question];
          if (colName) sheetData[colName] = answerData.answer.join(', ');
        }
        const { data: result, error } = await supabase.functions.invoke('sync-google-sheets', {
          body: { data: sheetData, sessionId: sessionIdRef.current, existingRow: sheetRowRef.current },
        });
        if (error) { console.error('Sync error:', error); return; }
        if (result?.rowNumber) {
          sheetRowRef.current = result.rowNumber;
          sessionStorage.setItem(`sheetRow_${sessionIdRef.current}`, String(result.rowNumber));
        }
      } catch (err) { console.error('Failed to sync:', err); }
      finally { syncInFlightRef.current = false; }
    });
  }, []);

  const syncToSheet = useCallback((isCompleted: boolean, destinationScreen?: number) => {
    const destScreen = destinationScreen ?? (currentScreen + 1);
    if (isCompleted) {
      if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
      pendingSyncRef.current = null;
      executeSync(true, destScreen);
      return;
    }
    pendingSyncRef.current = { isCompleted: false, destinationScreen: destScreen };
    if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
    syncDebounceRef.current = setTimeout(() => {
      const pending = pendingSyncRef.current;
      if (pending) { pendingSyncRef.current = null; executeSync(pending.isCompleted, pending.destinationScreen); }
    }, 1500);
  }, [currentScreen, executeSync]);

  return { syncToSheet };
}
